import React, { useContext, useEffect, useRef, useState } from 'react';
import ImageGallery from 'react-image-gallery';
import 'react-image-gallery/styles/css/image-gallery.css';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Grid2 as Grid, IconButton } from '@mui/material';
import { Skeleton } from '@mui/material';
import { isEmpty } from 'lodash';
import { ProductsStore } from '../../utils/stores/ProductsStore';

const gallerySx = {
    icon: {
        top: '50%',
        transform: 'translate(0, -50%)',
        color: { xs: 'common.white', sm: 'text.primary' },
        background: { xs: 'unset', sm: 'common.white' },
        zIndex: 7,
        opacity: 0.75,
        width: { xs: 20, sm: 'auto' },
        // Compound selector beats MUI's `.MuiButtonBase-root { position: relative }`,
        // which otherwise wins on injection order and leaves the arrows in normal flow.
        '&.MuiButtonBase-root': {
            position: 'absolute',
        },
        '&:hover': {
            background: { xs: 'unset', sm: 'common.white' },
            opacity: 0.95,
        },
        '& .MuiSvgIcon-root': {
            fontSize: { xs: 40, sm: '1.5rem' },
        },
    },
    // Keep left/right offsets on their own keys so neither arrow inherits the
    // other's horizontal anchor (a shared base `left` made the right arrow win
    // the emotion cascade tie and stack on the left edge on mobile).
    leftIcon: {
        left: { xs: '14px', sm: '28px' },
    },
    rightIcon: {
        right: { xs: '14px', sm: '28px' },
    },
    imageArea: {
        width: '100%',
        '& .image-gallery-slides img': {
            objectFit: 'cover',
            borderRadius: 0,
            height: 'inherit',
        },
        '& .image-gallery-content': {
            maxWidth: 1280,
            margin: 'auto',
        },
        '& .image-gallery.fullscreen-modal img': {
            height: 'unset',
        },
        '& .image-gallery.fullscreen-modal': {
            zIndex: 9,
        },
        borderRadius: { xs: 0, sm: 10 },
        overflow: 'hidden',
    },
    image: {
        maxHeight: 'unset',
    },
} as const;

export default function ProductImageGallery() {
    const productController = useContext(ProductsStore);
    const galleryElement = useRef<HTMLHeadingElement>(null);
    const styleElement = useRef<HTMLStyleElement | null>(null);
    const [startIndex, setStartIndex] = useState(0);
    const [isFullScreen, setIsFullScreen] = useState(false);
    const images = productController.product?.photos;

    const calcImageHeight = () => {
        const imageWidth = galleryElement.current?.clientWidth;
        return imageWidth ? imageWidth * 0.59 : 0;
    };

    const calcGalleryHeight = () => {
        const height = calcImageHeight();
        if (!height) return;
        if (!styleElement.current) {
            styleElement.current = document.createElement('style');
            document.head.appendChild(styleElement.current);
        }
        styleElement.current.textContent = `.image-gallery-content .image-gallery-slide .image-gallery-image { height: ${height}px; }`;
    };

    useEffect(() => {
        calcGalleryHeight();
        window.addEventListener('resize', calcGalleryHeight);
        return () => {
            window.removeEventListener('resize', calcGalleryHeight);
            if (styleElement.current) {
                styleElement.current.remove();
                styleElement.current = null;
            }
        };
    }, [images]);

    return (
        <>
            {!isEmpty(images) ? (
                <div ref={galleryElement} style={{ width: '100%' }}>
                    <Grid
                        size={{ xs: 12 }}
                        sx={gallerySx.imageArea}
                        style={{ width: '100%', zIndex: isFullScreen ? 9 : 7 }}
                    >
                        <ImageGallery
                            onScreenChange={setIsFullScreen}
                            startIndex={startIndex}
                            showPlayButton={false}
                            slideDuration={200}
                            swipeThreshold={25}
                            lazyLoad
                            showThumbnails={false}
                            useBrowserFullscreen={false}
                            items={(images || []).map((image) => ({
                                original: image.url,
                            }))}
                            renderLeftNav={(onClick, disabled) => (
                                <IconButton
                                    disabled={disabled}
                                    onClick={(event) => {
                                        onClick(event);
                                        setStartIndex((prev) => (prev === 0 ? (images || []).length - 1 : prev - 1));
                                    }}
                                    sx={[gallerySx.icon, gallerySx.leftIcon]}
                                >
                                    <ChevronLeftIcon />
                                </IconButton>
                            )}
                            renderRightNav={(onClick, disabled) => (
                                <IconButton
                                    disabled={disabled}
                                    onClick={(event) => {
                                        onClick(event);
                                        setStartIndex((prev) => (prev + 1) % (images || []).length);
                                    }}
                                    sx={[gallerySx.icon, gallerySx.rightIcon]}
                                >
                                    <ChevronRightIcon />
                                </IconButton>
                            )}
                        />
                    </Grid>
                </div>
            ) : (
                <div style={{ width: '100%' }} ref={galleryElement}>
                    <Skeleton width="100%" height={calcImageHeight()} />
                </div>
            )}
        </>
    );
}
